import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Select,
  HStack,
  VStack,
  Badge,
  Spinner,
  useColorModeValue,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useTable, usePagination } from 'react-table';
import * as XLSX from 'xlsx';
import NewMessageModal from './NewMessageModal';
import EngagementReportModal from './EngagementReportModal';
import MessageApi from '../../../../api/message';
import CompanyDropdownSelection from '../../../../functions/CompanyDropdownSelection';
import Card from 'components/card/Card';
import { prettifyTransferDate } from '../../../../functions/dateUtils';

// Channel type mapping for display
const CHANNEL_TYPES = {
  1: 'Push Notifications',
  2: 'WhatsApp (Registered)',
  3: 'WhatsApp (Unregistered)',
  4: 'WhatsApp (Phone Number)',
};

function MessageTable() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEngagementModalOpen, setIsEngagementModalOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const toast = useToast();

  // Filters
  const [filters, setFilters] = useState({
    month: '', // Format: YYYY-MM
    companyIds: [],
    channel: '',
    page: 1,
    resultsPerPage: 10,
  });


  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // Generate month options for the last 12 months
  const generateMonthOptions = () => {
    const months = [];
    const currentDate = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthValue = `${year}-${month.toString().padStart(2, '0')}`;

      const monthNames = [
        'ינואר',
        'פברואר',
        'מרץ',
        'אפריל',
        'מאי',
        'יוני',
        'יולי',
        'אוגוסט',
        'ספטמבר',
        'אוקטובר',
        'נובמבר',
        'דצמבר',
      ];

      const monthLabel = `${monthNames[date.getMonth()]} ${year}`;
      months.push({ value: monthValue, label: monthLabel });
    }

    return months;
  };

  const monthOptions = generateMonthOptions();

  const normalizePhoneNumbers = (phoneNumbers) => {
    if (!phoneNumbers) {
      return [];
    }

    const source = Array.isArray(phoneNumbers) ? phoneNumbers : String(phoneNumbers).split(',');

    return [...new Set(source.map((phone) => String(phone).trim()).filter(Boolean))];
  };

  const handleExportPhoneNumbers = (message) => {
    const phones = normalizePhoneNumbers(message.phoneNumbers);

    if (phones.length === 0) {
      toast({
        position: 'top',
        title: 'לא נמצאו מספרים לייצוא',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const worksheetData = phones.map((phone) => ({
      'מספר טלפון': phone,
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Phones');

    const datePart = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `message_${message.id}_phones_${datePart}.xlsx`);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'תאריך',
        accessor: 'createdDate',
        Cell: ({ value }) => (
          <Text fontSize="md" fontWeight="500">
            {prettifyTransferDate(value)}
          </Text>
        ),
      },
      {
        Header: 'ערוץ',
        accessor: 'channel',
        Cell: ({ value }) => (
          <Badge colorScheme="blue" variant="subtle" fontSize="sm">
            {CHANNEL_TYPES[value] || 'לא ידוע'}
          </Badge>
        ),
      },
      {
        Header: 'תוכן ההודעה',
        accessor: 'body',
        Cell: ({ value }) => (
          <Text fontSize="md" noOfLines={2} maxW="300px">
            {value || 'אין תוכן'}
          </Text>
        ),
      },
      {
        Header: 'חברות',
        accessor: 'companyNames',
        Cell: ({ value }) => (
          <Text fontSize="md" noOfLines={2}>
            {Array.isArray(value) ? value.join(', ') : 'אין חברות'}
          </Text>
        ),
      },
      {
        Header: 'נשלחו',
        accessor: 'userIdsCount',
        Cell: ({ value }) => (
          <Text fontSize="md" fontWeight="500">
            {value || 0}
          </Text>
        ),
      },
      {
        Header: 'בתהליך',
        accessor: 'inProcessCount',
        Cell: ({ value }) => (
          <Text fontSize="md" fontWeight="500">
            {value || 0}
          </Text>
        ),
      },
      {
        Header: 'נכשלו',
        accessor: 'failedCount',
        Cell: ({ value }) => (
          <Text fontSize="md" fontWeight="500">
            {value || 0}
          </Text>
        ),
      },
      {
        Header: 'פעולות',
        accessor: 'actions',
        Cell: ({ row }) => {
          const message = row.original;
          const hasPhoneNumbers = normalizePhoneNumbers(message.phoneNumbers).length > 0;

          return (
            <HStack spacing={2}>
              {message.channel === 1 && (
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => handleGenerateReport(message.id)}
                >
                  צור דוח מעורבות
                </Button>
              )}
              {hasPhoneNumbers && (
                <Button
                  size="sm"
                  colorScheme="green"
                  variant="outline"
                  onClick={() => handleExportPhoneNumbers(message)}
                >
                  ייצוא מספרים שנשלחו
                </Button>
              )}
            </HStack>
          );
        },
      },
    ],
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      pageCount,
      manualPagination: true,
      autoResetPage: false,
      autoResetPageIndex: false,
    },
    usePagination,
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const queryFilters = {
        ...filters,
        page: pageIndex + 1,
        resultsPerPage: pageSize,
      };

      // Convert month filter to startDate and endDate
      if (filters.month) {
        const [year, month] = filters.month.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0); // Last day of the month

        queryFilters.startDate = startDate.toISOString().split('T')[0];
        queryFilters.endDate = endDate.toISOString().split('T')[0];
        delete queryFilters.month; // Remove month from query as backend expects startDate/endDate
      }

      // Remove empty filters
      Object.keys(queryFilters).forEach((key) => {
        if (
          queryFilters[key] === '' ||
          queryFilters[key] === null ||
          queryFilters[key] === undefined
        ) {
          delete queryFilters[key];
        }
      });

      const response = await MessageApi.getMessagingHistory(queryFilters);
      if (response.data) {
        setData(response.data.messages || []);
        setPageCount(response.data.totalPages || 0);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching message history:', error);
      setData([]);
      setPageCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageIndex, pageSize, filters]);

  const handleTemplateSubmit = () => {
    setIsModalOpen(false);
    // Refresh data after sending a message
    fetchData();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Reset to first page when filtering
    gotoPage(0);
  };

  const handleGenerateReport = (messageId) => {
    setSelectedMessageId(messageId);
    setIsEngagementModalOpen(true);
  };

  return (
    <Card>
      <VStack spacing={4} align="stretch">
        {/* Header with New Message Button */}
        <Flex justify="space-between" align="center">
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            היסטוריית הודעות
          </Text>
          <Button variant="blue" onClick={() => setIsModalOpen(true)}>
            צור הודעה חדשה
          </Button>
        </Flex>

        {/* Filters */}
        <Box p={4} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
          <VStack spacing={3}>
            <HStack spacing={4} w="full" flexWrap="wrap">
              <Box minW="190px">
                <Text fontSize="md" mb={2}>
                  חודש
                </Text>
                <Select
                  value={filters.month}
                  onChange={(e) => handleFilterChange('month', e.target.value)}
                  size="md"
                  placeholder="כל החודשים"
                  width="190px"
                  pr="12px"
                >
                  {monthOptions.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box minW="190px">
                <Text fontSize="md" mb={2}>
                  ערוץ
                </Text>
                <Select
                  value={filters.channel}
                  onChange={(e) => handleFilterChange('channel', e.target.value)}
                  size="md"
                  placeholder="כל הערוצים"
                  width="190px"
                  pr="12px"
                >
                  <option value="1">Push Notifications</option>
                  <option value="2">WhatsApp (Registered)</option>
                  <option value="3">WhatsApp (Unregistered)</option>
                  <option value="4">WhatsApp (Phone Number)</option>
                </Select>
              </Box>
              <Box minW="190px">
                <Text fontSize="md" mb={2}>
                  חברות
                </Text>
                <CompanyDropdownSelection
                  showAll={false}
                  selectedCompany={(companies) =>
                    handleFilterChange('companyIds', companies.map((c) => c.id))
                  }
                  style={{ minWidth: 190 }}
                />
              </Box>
            </HStack>
          </VStack>
        </Box>

        {/* Results Summary */}
        <Text fontSize="md" color="gray.500" fontWeight="500">
          מציג {data.length} תוצאות מתוך {totalCount} סה"כ
        </Text>

        {/* Table */}
        <Box overflowX="auto">
          {loading ? (
            <Flex justify="center" p={8}>
              <Spinner size="lg" />
            </Flex>
          ) : (
            <Table {...getTableProps()} variant="simple" color="gray.500">
              <Thead>
                {headerGroups.map((headerGroup, index) => (
                  <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                    {headerGroup.headers.map((column, index) => (
                      <Th
                        {...column.getHeaderProps()}
                        pe="10px"
                        key={index}
                        borderColor={borderColor}
                      >
                        <Flex
                          justify="space-between"
                          align="center"
                          fontSize={{ sm: '14px', lg: '16px' }}
                          color="gray.600"
                          fontWeight="600"
                        >
                          {column.render('Header')}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody {...getTableBodyProps()}>
                {page.map((row, index) => {
                  prepareRow(row);
                  return (
                    <Tr {...row.getRowProps()} key={index}>
                      {row.cells.map((cell, index) => (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          fontSize={{ sm: '16px', md: '16px' }}
                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                          borderColor="transparent"
                        >
                          {cell.render('Cell')}
                        </Td>
                      ))}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>

        {/* Pagination */}
        <Flex justify="space-between" align="center" mt={4}>
          <HStack>
            <Text fontSize="md" fontWeight="500">
              הצג
            </Text>
            <NumberInput
              value={pageSize}
              onChange={(value) => setPageSize(Number(value))}
              min={5}
              max={50}
              w="80px"
              size="md"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text fontSize="md" fontWeight="500">
              תוצאות בעמוד
            </Text>
          </HStack>

          <HStack>
            <IconButton
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              icon={<ChevronRightIcon />}
              size="md"
              aria-label="First page"
            />
            <IconButton
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              icon={<ChevronRightIcon />}
              size="md"
              aria-label="Previous page"
            />
            <Text fontSize="md" fontWeight="500">
              עמוד {pageIndex + 1} מתוך {pageOptions.length}
            </Text>
            <IconButton
              onClick={() => nextPage()}
              disabled={!canNextPage}
              icon={<ChevronLeftIcon />}
              size="md"
              aria-label="Next page"
            />
            <IconButton
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
              icon={<ChevronLeftIcon />}
              size="md"
              aria-label="Last page"
            />
          </HStack>
        </Flex>

        <NewMessageModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleTemplateSubmit}
        />

        <EngagementReportModal
          isOpen={isEngagementModalOpen}
          onClose={() => setIsEngagementModalOpen(false)}
          messageId={selectedMessageId}
        />
      </VStack>
    </Card>
  );
}

export default MessageTable;
